// File: packages/agent-kit/src/conversations/useConversation/useChatSession.ts

import { useCallback, useRef, useState } from 'react';
import { agentTestChatService, isApiError } from '@dopomogai/api-client';
import { widgetsService } from '@dopomogai/api-client';
import type { InitTestChatResponse, UUID } from '@dopomogai/types';
import { AgentKitError } from '../../types';
import { sessionService } from '../../analytics/sessionService';

export type ConversationAuthMode = 'test' | 'widget';
export type WidgetInitOptions = {
  installationId?: string;
  widgetId?: string;
  templateId?: string;
  apiBaseUrlOverride?: string;
};

export type UseChatSessionOptions = {
  authMode?: ConversationAuthMode;
  widgetInit?: WidgetInitOptions;
  onSessionInit?: (config: InitTestChatResponse) => void;
};

function resolveApiBaseUrl(override?: string): string {
  if (override) return override;

  const globalApi = typeof window !== 'undefined' ? (window as any).__API_BASE_URL__ : undefined;
  if (globalApi) return globalApi;

  const viteEnv = typeof import.meta !== 'undefined' && (import.meta as any).env ? (import.meta as any).env.VITE_API_URL : undefined;
  if (viteEnv) return viteEnv;

  const nextEnv = typeof process !== 'undefined' && process.env ? process.env.NEXT_PUBLIC_API_URL : undefined;
  if (nextEnv) return nextEnv;

  const reactAppEnv = typeof process !== 'undefined' && process.env ? process.env.REACT_APP_API_URL : undefined;
  if (reactAppEnv) return reactAppEnv;

  const nxApi = typeof process !== 'undefined' && process.env ? process.env.NX_API_BASE_URL : undefined;
  if (nxApi) return nxApi;

  return 'http://localhost:8000';
}

export function useChatSession(
  initialAgentId: UUID | null,
  options?: UseChatSessionOptions
) {
  const { authMode = 'test', widgetInit, onSessionInit } = options || {};
  const sessionRef = useRef<InitTestChatResponse | null>(null);
  const [internalAgentId, setInternalAgentId] = useState<UUID | null>(initialAgentId);

  const ensureSession = useCallback(
    async (injectionContext?: string): Promise<{ session: InitTestChatResponse; agentIdForStream: UUID }> => {
      // Circuit breaker: If a session exists for the correct agent, reuse it.
      if (sessionRef.current) {
        const id = (authMode === 'widget' ? sessionRef.current.agent_id : internalAgentId) as UUID;
        if (id) return { session: sessionRef.current, agentIdForStream: id };
      }

      if (authMode === 'widget') {
        await sessionService.initialize();
        const visitor_id = sessionService.getVisitorId();

        const base = resolveApiBaseUrl(widgetInit?.apiBaseUrlOverride);
        if (!base) throw new AgentKitError('No API base URL available for widget init.');
        if (!widgetInit?.installationId && !widgetInit?.templateId && !widgetInit?.widgetId) {
          throw new AgentKitError('Widget initialization requires an installationId, templateId, or widgetId.');
        }
        const body: any = { visitor_id };
        if (widgetInit?.installationId) body.installation_id = widgetInit.installationId;
        if (widgetInit?.templateId) body.template_id = widgetInit.templateId;
        if (widgetInit?.widgetId) body.widget_id = widgetInit.widgetId;

        const wid = await widgetsService.initSession(base, body);
        if (isApiError(wid)) {
          throw new AgentKitError('Widget session initialization failed.', wid);
        }

        const cfg: InitTestChatResponse = {
          wat: wid.token,
          wat_expires_utc: new Date(wid.expUtc * 1000).toISOString(),
          api_base_url: wid.apiBaseUrl,
          agent_id: wid.agentId as UUID,
          agent_name: 'AI Assistant',
          enabled_tools: [],
          system_prompt_text: '',
          llm_model_id: '',
        } as InitTestChatResponse;

        sessionRef.current = cfg;
        (sessionRef.current as any).installation_id = wid.installationId;
        (sessionRef.current as any).widget_version_id = wid.versionId;
        (sessionRef.current as any).visitor_id = visitor_id;

        setInternalAgentId(wid.agentId as UUID);
        onSessionInit?.(cfg);
        return { session: cfg, agentIdForStream: wid.agentId as UUID };
      }

      const id = internalAgentId;
      if (!id) throw new AgentKitError('Agent ID is required for test mode.');
      const initResponse = await agentTestChatService.initSession(id, injectionContext);
      if (isApiError(initResponse)) {
        throw new AgentKitError('Session initialization failed.', initResponse);
      }

      const normalized = { ...(initResponse as any) };
      if (typeof normalized.wat_expires_utc === 'number') {
        try {
          normalized.wat_expires_utc = new Date(normalized.wat_expires_utc * 1000).toISOString();
        } catch {}
      }

      sessionRef.current = normalized as InitTestChatResponse;
      onSessionInit?.(normalized as InitTestChatResponse);
      return { session: initResponse as InitTestChatResponse, agentIdForStream: id };
    },
    [
      authMode,
      internalAgentId,
      widgetInit?.installationId,
      widgetInit?.widgetId,
      widgetInit?.templateId,
      widgetInit?.apiBaseUrlOverride,
      onSessionInit,
    ]
  );

  const applyOpenHeaders = useCallback(
    (headers: Record<string, string>, setConversationId: (id: UUID) => void) => {
      const lower = Object.fromEntries(Object.entries(headers || {}).map(([k, v]) => [k.toLowerCase(), v])) as Record<string, string>;
      const newWat = lower['x-new-wat'];
      const ttlStr = lower['x-new-wat-ttl'];
      const convId = lower['x-conversation-id'] as UUID | undefined;

      if (newWat && sessionRef.current) {
        sessionRef.current.wat = newWat;
        if (ttlStr) {
          const ttl = Number(ttlStr) || 0;
          const newExpiry = new Date(Date.now() + ttl * 1000).toISOString();
          (sessionRef.current as any).wat_expires_utc = newExpiry;
        }
      }
      if (convId) setConversationId(convId);
    },
    []
  );

  return {
    sessionRef,
    internalAgentId,
    setInternalAgentId,
    ensureSession,
    applyOpenHeaders,
  };
}
